import {
    Component,
    ChangeDetectionStrategy,
    ViewChild,
    ViewEncapsulation,
    TemplateRef,
    ChangeDetectorRef
} from "@angular/core";
import {IrbEditor} from "@irb/irb-editors/irb-editors.base";
import {IEvent, IrbEditorsEvents, IrbAppEvents, IrbDialogsEvents} from "@irb-common/events";
import {Observable, of, interval, Subscription} from "rxjs";
import {
    IAccountDetailSaveResponse,
    IMultipleAccountEditSaveResponse,
    ISaveMultipleAccountAsync
} from "../irb-editors.model";
import { IMultipleAccountEditRequest, IMultipleAccountEditDto } from '@irb/irb-clients/irb-clients.model';
import {IAlertDialogData,IUserDetails} from "@irb/app.model";
import {NumberTextBoxComponent} from "@irb-common/components/number-text-box/number-text-box.component";
import {AnalyticsService} from "@irb-common/analytics/analytics.service";
import { IGlobalSettings } from "@irb/irb-settings/irb-settings.model";
import { IrbSettingsService } from "@irb/irb-settings/irb-settings.service";
import { IrbSettingsEvents } from '@irb/irb-common/events';
import {DropdownFilterComponent} from "@irb-common/components/dropdown-filter/dropdown-filter.component";
import {IDropdownItem} from "@irb-common/models/dropdown.model";

@Component({ standalone: false,
    selector: "irb-editor-multiple-account",
    templateUrl: "./editor-multiple-accounts.component.html",
    styleUrls: ["./editor-multiple-accounts.component.scss", "../../irb-common/styles/dialog.scss"],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None
})
export class EditorMultipleAccountsComponent extends IrbEditor {
    @ViewChild("cashMinPerc") cashMinPerc: NumberTextBoxComponent;
    @ViewChild("cashTargetPerc") cashTargetPerc: NumberTextBoxComponent;
    @ViewChild("cashMaximumPerc") cashMaximumPerc: NumberTextBoxComponent;
    @ViewChild("cashMinDollar") cashMinDollar: NumberTextBoxComponent;
    @ViewChild("cashTargetDollar") cashTargetDollar: NumberTextBoxComponent;
    @ViewChild("cashMaximumDollar") cashMaximumDollar: NumberTextBoxComponent;
    @ViewChild('reinvestUnsupportedConfigMessage') reinvestUnsupportedConfigMessage: TemplateRef<any>;
    @ViewChild("trxCashMinSettingRef") trxCashMinSettingRef: any;
    @ViewChild("cashEquivSettingRef") cashEquivSettingRef: any;
    @ViewChild("trxCashMinValueRef") trxCashMinValueRef: NumberTextBoxComponent;

    defaultExpandedPanels: Array<string> = ["SPECIAL_TREATMENT", "CASH_NEEDS"];
    multipleAccountEditRequest: IMultipleAccountEditRequest;
    multipleAccountEditDto: IMultipleAccountEditDto;
    modelFlag: string = "model";
    cashMinimumModel: any;
    cashTargetModel: any;
    cashMaximumModel: any;
    cashPlaceholder: string;
    isUiTest = false;
    globalSettings: IGlobalSettings;
    checkSaveSub: Subscription;
    isDistHoldCashEnabled: boolean;
    isCashEqvEnabled: boolean;
    userDetails: IUserDetails;
    trxCashMinSettings: Array<IDropdownItem> = [
      {name: 'Use global targets', value: 'Set global'},
      {name: 'Set custom', value: 'Set custom'}
    ];
    trxCashMinSetting: IDropdownItem = null;
    trxCashMinValue: number = null;
    trxCashMinInd: string = null;
    cashEquivSettings: Array<IDropdownItem> = [
      {name: 'Use model targets', value: 'model'},
      {name: 'Use global targets', value: 'global'},
      {name: 'Set custom', value: 'custom'}
    ];
    cashEquivSetting: IDropdownItem = null;

    constructor(private cdr: ChangeDetectorRef, public ats: AnalyticsService, private iss: IrbSettingsService) {
        super();
      this.userDetails = this.as.getItem("IRB_APP_CONFIG") as IUserDetails;
      this.isDistHoldCashEnabled = this.userDetails?.isDistHoldCashEnabled || false;
      this.isCashEqvEnabled = this.userDetails?.isCashEqvEnabled || false;
    }

    get(event: IEvent) {
        super.get(event);
        this.ats.trackEditMultipleAccountsEvent();
        if (event.data) {
            this.multipleAccountEditRequest = event.data.data;

            this.getGlobalSettings().then(() => {
                let data = {
                    "isCapGainsReinvest": null,
                    "isDividendReinvest": null,
                    "isDoNotTrade": null,
                    "isGlobal": null,
                    "cashMaximum": null,
                    "cashTarget": null,
                    "cashMinimum": null,
                    "autoCashHoldForDist": null,
                    "globalCashMaximum": this.globalSettings.taxableCashMaxPerc + "%",
                    "globalCashTarget": this.globalSettings.taxableCashTargetPerc + "%",
                    "globalCashMinimum": this.globalSettings.taxableCashMinPerc + "%",
                    "trxCashMinValue": null,
                    "useModelCash": this.globalSettings.useModelCash,
                    "useModelCashAtAccount": null,
                    "isPercentage": null
                } as IMultipleAccountEditDto;

                this.load(data);
            });            
        }
    }

    load(data: any) {
        super.load(data);
        this.setCashMinimumTargetModel();
        this.ies.loadExpansionState(this.defaultExpandedPanels.length, "editor-multiple-account", this.defaultExpandedPanels);
        this.cdr.detectChanges();
        this.onResize();
    }

    populateTrxCashMinSettingDropdown(searchStr: string) {
        const f = DropdownFilterComponent.getFilteredList( searchStr, this.trxCashMinSettings);
        this.trxCashMinSettingRef.set(f);
    }

    populateCashEquivSettingDropdown(searchStr: string) {
        const f = DropdownFilterComponent.getFilteredList( searchStr, this.cashEquivSettings);
        this.cashEquivSettingRef.set(f);
    }

    setTrxCashMinSetting(event: IDropdownItem) {
      this.trxCashMinSetting = event;
      this.data.trxCashMinSetting = this.trxCashMinSetting.value;
      this.trxCashMinInd = 'P';
      this.data.trxCashMinInd = this.trxCashMinInd;
      if(this.trxCashMinSetting.value === 'Set custom') {
         this.data.trxCashMinValue = 0;
         this.trxCashMinValue = 0;
         this.trxCashMinInd = 'P';
         this.data.trxCashMinInd = this.trxCashMinInd;
      } else if (this.trxCashMinSetting.value === 'Set global') {
          this.data.trxCashMinValue = null;
          this.trxCashMinValue = null;
      }
      this.markAllAsTouched();
      this.onInputChange();
    }

    setCashEquivSetting(event: IDropdownItem) {
        this.cashEquivSetting = event;
        if (event?.value === 'model') {
          this.data.useModelCashAtAccount = true;
          this.data.isGlobal = false;
        } else if (event.value === 'global') {
          this.data.useModelCashAtAccount = false;
          this.data.isGlobal = true;
          this.data.isPercentage = null;
          this.data.useModelCashAtAccount = false;
        } else {
          this.data.useModelCashAtAccount = false;
          this.data.isGlobal = false;
        }
        this.setCashMinimumTargetModel();
    }

    onTrxCashMinValueChange() {
        this.data.trxCashMinValue = this.trxCashMinValue;
        this.onInputChange();
    }

    onInputChange() {
        if(this.data) {
            let ignoreList: Array<string> = [];

            if (this.data.useModelCash && this.data.isGlobal) {
                ignoreList.push("globalCashMinimum");
                ignoreList.push("globalCashTarget");
                ignoreList.push("globalCashMaximum");
            }

            setTimeout(() => {
                if(!this.data.isGlobal) {
                    if (this.data.isPercentage) {
                        this.isValid = (!this.cashMinPerc?.inputControl.invalid || false) && (!this.cashTargetPerc?.inputControl.invalid || false) && (!this.cashMaximumPerc?.inputControl.invalid || false);
                    } else {
                        this.isValid = (!this.cashMinDollar?.inputControl.invalid || false) && (!this.cashTargetDollar?.inputControl.invalid || false) && (!this.cashMaximumDollar?.inputControl.invalid || false);              
                    }
                } else {
                    this.isValid = true;
                }
                if (this.trxCashMinSetting?.value === 'Set custom') {
                  this.isValid = this.isValid && (!this.trxCashMinValueRef?.inputControl.invalid || false);
                }
            });

            super.onInputChange(ignoreList);
        }     
    }

    onExpressAs() {
        this.markAllAsTouched();

        setTimeout(() => {
            this.onInputChange();
        });
    }

    onExpressCashMinAs() {
      this.data.trxCashMinInd = this.trxCashMinInd;
      this.markAllAsTouched();

      setTimeout(() => {
        this.onInputChange();
      });
    }
    setCashMinimumTargetModel() {
        if ((this.data.useModelCash && this.data.isGlobal) || this.data.useModelCashAtAccount) {                        
            this.cashPlaceholder = this.modelFlag
            this.cashMinimumModel = null;
            this.cashTargetModel = null;
            this.cashMaximumModel = null;
        } else if(this.data.isGlobal) {
            this.cashPlaceholder = 'Various';
            this.cashMinimumModel = null;
            this.cashTargetModel = null;
            this.cashMaximumModel = null;        
        } else if(this.data.isGlobal == false && this.data.useModelCashAtAccount == false) {            
            this.cashPlaceholder = '';
            this.data.isPercentage = true;
        } else {
            this.cashPlaceholder = '';
        }

        this.markAllAsTouched();
        
        setTimeout(() => {
            this.onInputChange();
        });
    }

    markAllAsTouched() {
        setTimeout(() => {
            this.cashMinPerc?.inputControl.markAsTouched();
            this.cashTargetPerc?.inputControl.markAsTouched();
            this.cashMaximumPerc?.inputControl.markAsTouched();
            this.cashMinDollar?.inputControl.markAsTouched();
            this.cashTargetDollar?.inputControl.markAsTouched();
            this.cashMaximumDollar?.inputControl.markAsTouched();
            this.trxCashMinValueRef?.inputControl.markAsTouched();
        });
    }

    save() {
        this.validateInput().subscribe(errors => {
            if (errors.length > 0) {
                this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                    msg: errors[0],
                    dType: "ERROR"
                } as IAlertDialogData);
            } else {
                this.as.fireEvent(IrbEditorsEvents.SAVE_START);                
                this.multipleAccountEditRequest.accountDto = this.data;

                this.ies.saveMultipleAccountsAsync(this.multipleAccountEditRequest).subscribe((data: ISaveMultipleAccountAsync) => {
                    this.checkSaveMultipleAccountsStatus(data.rprasid);
                }, () => {
                    this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);
                });        
            }
        });
    }

    checkSaveMultipleAccountsStatus(rprasId: string) {
        const intervalValue = this.isUiTest ? 0 : 3000;
        this.checkSaveSub = interval(intervalValue).subscribe(() => {
            this.ies.checkSaveMultipleAccountsStatus(rprasId).subscribe((response: IMultipleAccountEditSaveResponse) => {
                if(response.status.toUpperCase() === "COMPLETED") {
                    this.checkSaveSub.unsubscribe();
                    this.completeSaveMultipleAccounts({
                        responseStatus: "SUCCESS"
                    } as IAccountDetailSaveResponse);
                } else if(response.status.toUpperCase() === "FAILED") {
                    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                        dType: 'ERROR',
                        dTitle: '',
                        dProceedBtnLabel: '',
                        msg: response.failMsg || "An error occurred processing your request.",
                        onProceed: () => {}
                    } as IAlertDialogData);
                    this.checkSaveSub.unsubscribe();
                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                } else if(response.status.toUpperCase() === "INPROGRESS") {
                }
            });
        });
    }

    completeSaveMultipleAccounts(res: IAccountDetailSaveResponse) {
        this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);

        if (res.responseStatus === "SUCCESS") {
            this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
                "hasPendingFunction": this.hasPendingFunction,
                "data": "Multiple accounts updated."
            });
            this.as.fireEvent(IrbEditorsEvents.ACCOUNT_SAVED);
            this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
            this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
        }
    }

    validateInput(): Observable<string[]> {
        const errors: string[] = [];

        if(this.cashMinPerc?.inputControl.invalid) {
            errors.push("Enter a valid Cash Minimum value.");
        }

        if(this.cashTargetPerc?.inputControl.invalid) {
            errors.push("Enter a valid Cash Target value.");
        }

        if(this.cashMaximumPerc?.inputControl.invalid) {
            errors.push("Enter a valid Cash Maximum value.");
        }

        if(this.cashMinDollar?.inputControl.invalid) {
            errors.push("Enter a valid Cash Minimum value.");
        }

        if(this.cashTargetDollar?.inputControl.invalid) {
            errors.push("Enter a valid Cash Target value.");
        }

        if(this.cashMaximumDollar?.inputControl.invalid) {
            errors.push("Enter a valid Cash Maximum value.");
        }
        return of(errors);
    }

    onCashNeedsMinInputChange() {
        let displayVal = '';

        if (this.data.isPercentage) {
            let v = this.cashMinPerc?.inputControl.value !== undefined && this.cashMinPerc?.inputControl.value !== null;
            displayVal = (!this.cashMinPerc?.inputControl.invalid && v) ? this.cashMinPerc?.inputControl.value + '%' : null;
        } else {
            let v = this.cashMinDollar?.inputControl.value !== undefined && this.cashMinDollar?.inputControl.value !== null;
            displayVal = (!this.cashMinDollar?.inputControl.invalid && v) ? '$' + this.cashMinDollar?.inputControl.value : null;
        }
        
        if(this.data.cashMinimum !== displayVal) {
            this.data.cashMinimum = this.getCashNeedsForSave(displayVal);            
            this.onInputChange();
        }
    }

    onCashNeedsTargetInputChange() {
        let displayVal = '';
        
        if (this.data.isPercentage) {
            let v = this.cashTargetPerc?.inputControl.value !== undefined && this.cashTargetPerc?.inputControl.value !== null;
            displayVal = (!this.cashTargetPerc?.inputControl.invalid && v) ? this.cashTargetPerc?.inputControl.value + '%' : null;
        } else {
            let v = this.cashTargetDollar?.inputControl.value !== undefined && this.cashTargetDollar?.inputControl.value !== null;
            displayVal = (!this.cashTargetDollar?.inputControl.invalid && v) ? '$' + this.cashTargetDollar?.inputControl.value : null;
        }

        if(this.data.cashTarget !== displayVal) {
            this.data.cashTarget = this.getCashNeedsForSave(displayVal);
            this.onInputChange();
        }
    }

    onCashNeedsMaxInputChange() {
        let displayVal = '';

        if (this.data.isPercentage) {
            let v = this.cashMaximumPerc?.inputControl.value !== undefined && this.cashMaximumPerc?.inputControl.value !== null;
            displayVal = (!this.cashMaximumPerc?.inputControl.invalid && v) ? this.cashMaximumPerc?.inputControl.value+ '%' : null;
        } else {
            let v = this.cashMaximumDollar?.inputControl.value !== undefined && this.cashMaximumDollar?.inputControl.value !== null;
            displayVal = (!this.cashMaximumDollar?.inputControl.invalid && v) ? '$' + this.cashMaximumDollar?.inputControl.value : null;
        }
        
        if(this.data.cashMaximum !== displayVal) {
            this.data.cashMaximum = this.getCashNeedsForSave(displayVal);            
            this.onInputChange();
        }
    }

    getCashNeedsForSave(value: any) {
        if (value && value.includes("$-")) {
            value = value.replace("$-", "-$");
            return value;
        }
        return value;
    }

    validateReinvestChange(_event: any) {
        const isReinvestValid = !(
            (this.data.isDividendReinvest === true && (this.data.isCapGainsReinvest === false || this.data.isCapGainsReinvest === null)) ||
            (this.data.isDividendReinvest === null && this.data.isCapGainsReinvest === false)
        );
        
        if(!isReinvestValid) {
            this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                msg: "You have selected an unsupported reinvestment election.",
                altMsgTemplate: this.reinvestUnsupportedConfigMessage,
                dType: "WARN"
            } as IAlertDialogData);
          
            setTimeout(() => {
                this.data.isCapGainsReinvest = true;
                this.onInputChange();
            });
        } else {
            this.onInputChange();
        }
      }

      toggleUseGlobalDefault(_event: any) {
        if (this.data.isGlobal) {
            this.data.isPercentage = null;
            this.data.useModelCashAtAccount = false;
        }

        this.setCashMinimumTargetModel();
        this.onInputChange();
      }

      toggleUseModelCash(_event: any) {
        if (this.data.useModelCashAtAccount) {
            this.data.isPercentage = null;
            this.data.isGlobal = false;
        }

        this.setCashMinimumTargetModel();
        this.onInputChange();
      }

      getGlobalSettings() {
        return new Promise((resolve: any) => {
          this.iss.getGlobalSettings().subscribe((data) => {
            this.globalSettings = data as IGlobalSettings;
            resolve();
          });
        });
      }

      openGlobalSettings() {
        this.as.fireEvent(IrbSettingsEvents.SHOW, {
            defaultTabIndex: 1,
            onFirstLoad: () => { 
                setTimeout(() => { 
                  this.as.fireEvent(IrbSettingsEvents.EXPAND_AUTO_CASH);
                  document.getElementById("AUTO_CASH")?.scrollIntoView(); 
                });
              }
        });
      }

      processEvent(event: IEvent) {
        super.processEvent(event);
    
        switch(event.type) {
          case IrbSettingsEvents.GLOBAL_SETTINGS_REFRESH:
            this.getGlobalSettings();
            break;
        }
      }

      showAccountMultieditView() {
        this.as.fireEvent(IrbDialogsEvents.SHOW_ACCOUNT_MULTIEDIT_VIEW, {
            data: this.util.clone(this.multipleAccountEditRequest)
        });
      }
}
